import React, { useEffect, useRef, useState } from 'react'
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'

const Testimonials = () => {
  const { t } = useTranslation()
  const containerRef = useRef(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [direction, setDirection] = useState(0)
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  })

  const headerY = useTransform(scrollYProgress, [0, 1], [0, -50])
  const headerOpacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [1, 1, 0.8, 0.4])

  const testimonials = [
    {
      id: 1,
      name: t('testimonials.items.sarah.name'),
      role: t('testimonials.items.sarah.role'),
      content: t('testimonials.items.sarah.content'),
      rating: 5,
      gradient: "from-purple-400/20 via-pink-400/20 to-rose-400/20",
      logo: (
        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
        </svg>
      )
    },
    {
      id: 2,
      name: t('testimonials.items.marcus.name'),
      role: t('testimonials.items.marcus.role'),
      content: t('testimonials.items.marcus.content'),
      rating: 5,
      gradient: "from-blue-400/20 via-cyan-400/20 to-teal-400/20",
      logo: (
        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
        </svg>
      )
    },
    {
      id: 3,
      name: t('testimonials.items.jennifer.name'),
      role: t('testimonials.items.jennifer.role'),
      content: t('testimonials.items.jennifer.content'),
      rating: 5,
      gradient: "from-emerald-400/20 via-green-400/20 to-lime-400/20",
      logo: (
        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
        </svg>
      )
    },
    {
      id: 4,
      name: t('testimonials.items.david.name'),
      role: t('testimonials.items.david.role'),
      content: t('testimonials.items.david.content'),
      rating: 5,
      gradient: "from-orange-400/20 via-amber-400/20 to-yellow-400/20",
      logo: (
        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      )
    },
    {
      id: 5,
      name: t('testimonials.items.lisa.name'),
      role: t('testimonials.items.lisa.role'),
      content: t('testimonials.items.lisa.content'),
      rating: 5,
      gradient: "from-violet-400/20 via-purple-400/20 to-fuchsia-400/20",
      logo: (
        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
        </svg>
      )
    }
  ]

  // Auto-advance testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1)
      setCurrentIndex((prevIndex) => 
        prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
      )
    }, 5000)

    return () => clearInterval(timer)
  }, [])

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.8,
      rotateY: direction > 0 ? 45 : -45
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
      rotateY: 0
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.8,
      rotateY: direction < 0 ? 45 : -45
    })
  }

  const swipeConfidenceThreshold = 10000
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity
  }

  const paginate = (newDirection) => {
    setDirection(newDirection)
    if (newDirection === 1) {
      setCurrentIndex(currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1)
    } else {
      setCurrentIndex(currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1)
    }
  }

  const goToSlide = (index) => {
    setDirection(index > currentIndex ? 1 : -1)
    setCurrentIndex(index)
  }

  return (
    <section ref={containerRef} className="relative min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-10 overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0">
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"
        />
        <motion.div 
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.15, 0.25, 0.15]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"
        />
      </div>

      {/* Header */}
      <motion.div 
        style={{ y: headerY, opacity: headerOpacity }}
        className="text-center mb-12"
      >
        <motion.div
          initial={{ opacity: 0, y: -80, scale: 0.8 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ 
            duration: 1.2, 
            ease: "easeOut",
            type: "spring",
            stiffness: 100,
            damping: 15
          }}
          viewport={{ once: true }}
        >
          <h2 className="text-xl md:text-7xl font-bold bg-gradient-to-r from-white via-purple-200 to-blue-200 bg-clip-text text-transparent mb-6">
            {t('testimonials.title')}
          </h2>
          <p className="text-white/70 text-xl md:text-2xl max-w-3xl mx-auto font-light leading-relaxed">
            {t('testimonials.subtitle')}
          </p>
        </motion.div>
      </motion.div>

      {/* Horizontal Testimonials Carousel */}
      <div className="relative z-10 px-4 md:px-8 max-w-6xl mx-auto">
        <div className="relative h-[600px] flex items-center justify-center perspective-1000">
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.5 },
                scale: { duration: 0.5 },
                rotateY: { duration: 0.8 }
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x)
                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1)
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1)
                }
              }}
              className="absolute w-full max-w-3xl mx-auto cursor-grab active:cursor-grabbing"
            >
              {/* Glassmorphism Card */}
              <div className={`relative backdrop-blur-xl bg-gradient-to-br ${testimonials[currentIndex].gradient} border border-white/20 rounded-3xl p-8 md:p-12 shadow-2xl overflow-hidden transform-gpu`}>
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-0 left-0 w-40 h-40 bg-gradient-to-br from-white/20 to-transparent rounded-full blur-2xl animate-pulse"></div>
                  <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-white/15 to-transparent rounded-full blur-xl animate-pulse" style={{animationDelay: '1s'}}></div>
                </div>

                {/* Content */}
                <div className="relative z-10">
                  {/* Quote Icon */}
                  <motion.div 
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.8, type: "spring" }}
                    className="mb-8"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-white/20 to-white/10 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30">
                      <svg className="w-8 h-8 text-white/80" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                      </svg>
                    </div>
                  </motion.div>

                  {/* Testimonial Text */}
                  <motion.p 
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.8 }}
                    className="text-white/90 text-lg md:text-xl leading-relaxed mb-8 font-light tracking-wide"
                  >
                    "{testimonials[currentIndex].content}"
                  </motion.p>

                  {/* Rating Stars */}
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                    className="flex items-center mb-8"
                  >
                    {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                      <motion.svg
                        key={i}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5 + (i * 0.1), duration: 0.3 }}
                        className="w-6 h-6 text-yellow-400 mr-1 drop-shadow-lg"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </motion.svg>
                    ))}
                  </motion.div>

                  {/* Author Info */}
                  <motion.div 
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6, duration: 0.8 }}
                    className="flex items-center"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-white/25 to-white/10 rounded-full flex items-center justify-center mr-5 backdrop-blur-sm border border-white/30 shadow-lg">
                      {testimonials[currentIndex].logo}
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-lg tracking-wide">
                        {testimonials[currentIndex].name}
                      </h4>
                      <p className="text-white/70 text-sm font-medium">
                        {testimonials[currentIndex].role}
                      </p>
                      <p className="text-white/50 text-sm">
                        {testimonials[currentIndex].company}
                      </p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Arrows */}
          <button
            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 hover:scale-110"
            onClick={() => paginate(-1)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 hover:scale-110"
            onClick={() => paginate(1)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center mt-8 space-x-3">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-white scale-125' 
                  : 'bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>

        {/* Side Preview Cards */}
        <div className="hidden lg:block">
          {/* Previous Card Preview */}
          <motion.div 
            className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-64 opacity-30 pointer-events-none"
            animate={{ x: [-20, 0, -20] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className={`backdrop-blur-xl bg-gradient-to-br ${testimonials[currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1].gradient} border border-white/10 rounded-2xl p-6 scale-75`}>
              <p className="text-white/70 text-sm mb-4 line-clamp-3">
                "{testimonials[currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1].content}"
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mr-3">
                  {testimonials[currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1].logo}
                </div>
                <div>
                  <h5 className="text-white text-sm font-medium">
                    {testimonials[currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1].name}
                  </h5>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Next Card Preview */}
          <motion.div 
            className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1/2 w-64 opacity-30 pointer-events-none"
            animate={{ x: [20, 0, 20] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          >
            <div className={`backdrop-blur-xl bg-gradient-to-br ${testimonials[currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1].gradient} border border-white/10 rounded-2xl p-6 scale-75`}>
              <p className="text-white/70 text-sm mb-4 line-clamp-3">
                "{testimonials[currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1].content}"
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mr-3">
                  {testimonials[currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1].logo}
                </div>
                <div>
                  <h5 className="text-white text-sm font-medium">
                    {testimonials[currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1].name}
                  </h5>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 w-64 h-1 bg-white/20 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-purple-400 to-blue-400 rounded-full"
          initial={{ width: "0%" }}
          animate={{ width: "100%" }}
          transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
          key={currentIndex}
        />
      </div>
    </section>
  )
}

export default Testimonials